import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./api";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

import { useI18n } from "./i18n/I18nContext";

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .ua-page {
    min-height: 100vh;
    background: #000;
    background-image:
      radial-gradient(ellipse 80% 50% at 50% -20%, rgba(230,255,62,0.07) 0%, transparent 60%),
      repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,0.02) 40px),
      repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,255,255,0.02) 40px);
    color: #fff;
    padding: 48px 20px;
    display: flex;
    justify-content: center;
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
    box-sizing: border-box;
  }

  .ua-wrapper {
    width: 100%;
    max-width: 860px;
  }

  /* ── Title ── */
  .ua-title {
    font-size: 13px;
    font-weight: 900;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #fff;
    margin: 0 0 4px;
    display: inline-block;
  }
  .ua-title::after {
    content: '';
    display: block;
    height: 2px;
    width: 100%;
    background: linear-gradient(90deg, #E6FF3E 0%, transparent 100%);
    margin-top: 6px;
    border-radius: 2px;
  }

  /* ── Back button ── */
  .ua-btn-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 16px 0 22px;
    background: transparent;
    border: 1px solid rgba(230,255,62,0.35);
    color: #E6FF3E;
    padding: 7px 14px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .ua-btn-back:hover {
    background: rgba(230,255,62,0.08);
    border-color: #E6FF3E;
    box-shadow: 0 0 14px rgba(230,255,62,0.15);
    transform: translateX(-2px);
  }

  /* ── Alerts ── */
  .ua-alert {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    animation: ua-slide-down 0.25s ease;
  }
  .ua-alert-error {
    background: rgba(180,30,30,0.15);
    border: 1px solid rgba(255,80,80,0.3);
    color: #ff9a9a;
  }
  .ua-alert-success {
    background: rgba(30,180,80,0.12);
    border: 1px solid rgba(50,220,100,0.3);
    color: #80ffaa;
  }
  @keyframes ua-slide-down {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Cards ── */
  .ua-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    padding: 22px;
    margin-bottom: 20px;
    backdrop-filter: blur(12px);
    transition: border-color 0.3s ease;
  }
  .ua-card:hover {
    border-color: rgba(230,255,62,0.14);
  }
  .ua-card-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.35);
    margin: 0 0 18px;
  }

  /* ── Form grid ── */
  .ua-form-grid {
    display: grid;
    grid-template-columns: 2fr 2fr 1.4fr auto;
    gap: 10px;
    align-items: center;
  }

  /* ── Inputs ── */
  .ua-input {
    background: rgba(255,255,255,0.05);
    color: #fff;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 13px;
    font-family: inherit;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    outline: none;
  }
  .ua-input::placeholder { color: rgba(255,255,255,0.28); }
  .ua-input:focus {
    border-color: #E6FF3E;
    background: rgba(230,255,62,0.04);
    box-shadow: 0 0 0 3px rgba(230,255,62,0.08);
  }

  /* ── Password wrapper ── */
  .ua-pwd-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }
  .ua-pwd-wrapper .ua-input { padding-right: 38px; }
  .ua-eye-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    color: rgba(255,255,255,0.35);
    display: flex;
    align-items: center;
    transition: color 0.2s;
  }
  .ua-eye-btn:hover { color: #E6FF3E; }

  /* ── Select ── */
  .ua-select {
    appearance: none;
    background-color: rgba(255,255,255,0.05);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7'%3E%3Cpath d='M1 1l4.5 4.5L10 1' stroke='%23E6FF3E' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 11px;
    color: #fff;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px;
    padding: 9px 30px 9px 12px;
    font-size: 13px;
    font-family: inherit;
    width: 100%;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    outline: none;
    box-sizing: border-box;
  }
  .ua-select:focus {
    border-color: #E6FF3E;
    box-shadow: 0 0 0 3px rgba(230,255,62,0.08);
  }
  .ua-select:hover { border-color: rgba(230,255,62,0.4); }
  .ua-select option { background: #111; color: #fff; }

  /* ── Select small (table) ── */
  .ua-select-sm {
    font-size: 12px;
    padding: 5px 26px 5px 10px;
  }

  /* ── Buttons ── */
  .ua-btn-add {
    background: #E6FF3E;
    color: #000;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    font-weight: 800;
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    white-space: nowrap;
    font-family: inherit;
    transition: all 0.2s ease;
    box-shadow: 0 2px 14px rgba(230,255,62,0.22);
  }
  .ua-btn-add:hover {
    background: #f0ff6a;
    box-shadow: 0 4px 22px rgba(230,255,62,0.4);
    transform: translateY(-1px);
  }
  .ua-btn-add:active { transform: translateY(0); }

  .ua-btn-edit {
    background: transparent;
    color: #E6FF3E;
    border: 1px solid rgba(230,255,62,0.45);
    border-radius: 6px;
    padding: 5px 12px;
    font-size: 11px;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.04em;
    cursor: pointer;
    margin-right: 6px;
    transition: all 0.2s ease;
  }
  .ua-btn-edit:hover {
    background: rgba(230,255,62,0.1);
    border-color: #E6FF3E;
    box-shadow: 0 0 10px rgba(230,255,62,0.15);
  }

  .ua-btn-delete {
    background: rgba(170,0,0,0.65);
    color: #fff;
    border: 1px solid rgba(220,50,50,0.35);
    border-radius: 6px;
    padding: 5px 12px;
    font-size: 11px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .ua-btn-delete:hover {
    background: #cc1111;
    border-color: #ff4444;
    box-shadow: 0 0 12px rgba(255,50,50,0.2);
  }

  /* ── Table ── */
  .ua-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .ua-table thead tr {
    border-bottom: 1px solid rgba(230,255,62,0.18);
  }
  .ua-table thead th {
    padding: 8px 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.3);
    text-align: left;
  }
  .ua-table thead th:last-child { text-align: right; }
  .ua-table tbody tr { transition: background 0.15s ease; }
  .ua-table tbody tr:hover { background: rgba(230,255,62,0.03); }
  .ua-table td {
    padding: 10px;
    border-top: 1px solid rgba(255,255,255,0.05);
    vertical-align: middle;
  }
  .ua-table td:last-child { text-align: right; }

  .ua-user-login {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 13px;
  }
  .ua-user-login::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #E6FF3E;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 0 6px rgba(230,255,62,0.6);
  }

  /* ── Loading / Empty ── */
  .ua-loading {
    text-align: center;
    padding: 32px 0;
    color: rgba(255,255,255,0.3);
    font-size: 13px;
    letter-spacing: 0.08em;
    animation: ua-pulse 1.5s ease-in-out infinite;
  }
  @keyframes ua-pulse {
    0%, 100% { opacity: 0.4; }
    50%       { opacity: 1; }
  }
  .ua-empty {
    text-align: center;
    padding: 32px 0;
    color: rgba(255,255,255,0.22);
    font-size: 13px;
  }

  /* ── Responsive ── */
  @media (max-width: 640px) {
    .ua-form-grid {
      grid-template-columns: 1fr 1fr;
    }
    .ua-form-grid > *:nth-child(3) { grid-column: span 2; }
    .ua-form-grid > *:nth-child(4) { grid-column: span 2; }
  }
`;

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════════ */
const ROLES = [
  "super_admin",
  "admin",
  "operateur_logistique",
  "operateur_production",
  "operateur_qualite",
  "operateur_production&qualite",
  "operateur_rd",
  "operateur_0",
];

const ROLE_OPTIONS = [
  "super_admin",
  "admin",
  "operateur_logistique",
  "operateur_production&qualite",
  "operateur_qualite",
  "operateur_rd",
  "operateur_0",
];

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
export default function UserAdmin() {
  const { t, ui } = useI18n();

  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [editedRoles, setEditedRoles]   = useState({});
  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "operateur_0",
  });

  const navigate = useNavigate();

  /* ── Load users ── */
  async function loadUsers() {
    setLoading(true);
    setError("");
    try {
      const res  = await apiFetch("/api/admin/users");
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.message || "Erreur");
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const token   = localStorage.getItem("authToken");
    const rawUser = localStorage.getItem("user");
    if (!token || !rawUser) { navigate("/"); return; }
    const current = JSON.parse(rawUser);
    if (current.role !== "super_admin") setError("Accès réservé à l'administrateur.");
    else loadUsers();
  }, [navigate]);

  /* ── Handlers ── */
  function handleChange(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleAdd(e) {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!form.username || !form.password) {
      setError("Login et mot de passe sont obligatoires."); return;
    }
    try {
      const res  = await apiFetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.message || "Erreur");
      setForm({ username: "", password: "", role: "operateur_0" });
      setSuccess("Utilisateur ajouté avec succès.");
      loadUsers();
    } catch (e) {
      setError(e.message || "Erreur lors de l'ajout");
    }
  }

  async function handleUpdate(u) {
    const newRole  = editedRoles[u.username] ?? u.role ?? "operateur_0";
    const changePwd = window.confirm("Changer le mot de passe ?");
    let pwd = "";
    if (changePwd) {
      pwd = window.prompt("Nouveau mot de passe (laisser vide pour annuler)") || "";
      if (!pwd) return;
    }
    setError(""); setSuccess("");
    try {
      const res  = await apiFetch(`/api/admin/users/${encodeURIComponent(u.username)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: u.username, role: newRole, password: pwd || undefined }),
      });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.message || "Erreur");
      setSuccess("Utilisateur mis à jour.");
      setEditedRoles((prev) => ({ ...prev, [u.username]: newRole }));
      loadUsers();
    } catch (e) {
      setError(e.message || "Erreur mise à jour");
    }
  }

  async function handleDelete(u) {
    if (!window.confirm(`Supprimer l'utilisateur "${u.username}" ?`)) return;
    setError(""); setSuccess("");
    try {
      const res  = await apiFetch(`/api/admin/users/${encodeURIComponent(u.username)}`, { method: "DELETE" });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.message || "Erreur");
      setSuccess("Utilisateur supprimé.");
      loadUsers();
    } catch (e) {
      setError(e.message || "Erreur suppression");
    }
  }

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
    <>
      {/* Injection CSS */}
      <style>{CSS}</style>

      <div className="ua-page">
        <div className="ua-wrapper">

          {/* ── Header ── */}
          <h1 className="ua-title">{t("admin.title")}</h1>

          <button className="ua-btn-back" onClick={() => navigate("/menu")}>
            ← Retour au menu
          </button>

          {/* ── Alerts ── */}
          {error   && <div className="ua-alert ua-alert-error">{error}</div>}
          {success && <div className="ua-alert ua-alert-success">{success}</div>}

          {/* ── Formulaire ajout ── */}
          <form className="ua-card" onSubmit={handleAdd}>
            <p className="ua-card-title">{t("admin.addUser")}</p>
            <div className="ua-form-grid">

              <input
                className="ua-input"
                placeholder="Login"
                value={form.username}
                onChange={(e) => handleChange("username", e.target.value)}
              />

              <div className="ua-pwd-wrapper">
                <input
                  className="ua-input"
                  placeholder="Mot de passe"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                />
                <button
                  type="button"
                  className="ua-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword
                    ? <AiOutlineEyeInvisible size={17} />
                    : <AiOutlineEye size={17} />}
                </button>
              </div>

              <select
                className="ua-select"
                value={form.role}
                onChange={(e) => handleChange("role", e.target.value)}
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>

              <button type="submit" className="ua-btn-add">{ui("Ajouter")}</button>
            </div>
          </form>

          {/* ── Liste utilisateurs ── */}
          <div className="ua-card">
            <p className="ua-card-title">Utilisateurs existants</p>

            {loading ? (
              <div className="ua-loading">{ui("Chargement…")}</div>
            ) : users.length === 0 ? (
              <div className="ua-empty">Aucun utilisateur.</div>
            ) : (
              <table className="ua-table">
                <thead>
                  <tr>
                    <th>Login</th>
                    <th>Rôle</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.username}>
                      <td>
                        <span className="ua-user-login">{u.username}</span>
                      </td>
                      <td>
                        <select
                          className={`ua-select ua-select-sm`}
                          value={editedRoles[u.username] ?? u.role ?? "operateur_0"}
                          onChange={(e) =>
                            setEditedRoles((prev) => ({ ...prev, [u.username]: e.target.value }))
                          }
                        >
                          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td>
                        <button className="ua-btn-edit"   onClick={() => handleUpdate(u)}>{ui("Modifier")}</button>
                        <button className="ua-btn-delete" onClick={() => handleDelete(u)}>{ui("Supprimer")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

        </div>
      </div>
    </>
  );
}